<template>
  <div class="tournament-embed-view" :style="backgroundStyle">
    <div v-if="isRedirecting" class="redirect-overlay">
      <v-progress-circular
        indeterminate
        size="60"
        width="5"
        color="primary"
      />
    </div>
    <!-- Check if this should render in minimal mode -->
    <template v-if="showMinimalView">
      <!-- Minimal View - Only show timer and leaderboard -->
      <v-col class="tournament-embed-header d-flex justify-end align-end px-0">
        <LanguageSelector :allowed-languages="embedConfig.allowedLangs"></LanguageSelector>
      </v-col>

      <!-- Show only the leaderboard table -->
      <v-row class="mx-0">
        <v-col cols="12">
          <Table :items="displayParticipants" :expandable="tournamentData?.tournamentSettings?.publicPerformance === true" :blurred="antiSpoiler"
              :serverPagination="true"
              :serverTotalPages="serverTotalPages"
              :serverCurrentPage="serverCurrentPage"
              :serverTotalElements="serverTotalElements"
              :participantsLoading="participantsLoading"
              @page-change="$emit('page-change', $event)"
              @search-change="$emit('search-change', $event)" />
        </v-col>
      </v-row>

    </template>

    <!-- Regular view for other tournaments -->
    <template v-else>
      <!-- Basic tournament header with different mobile presentation -->
      <v-col class="tournament-embed-header px-0" :class="{ 'is-finished': showEndedTimerLabel }">
        <!-- Desktop header layout -->
        <v-col class="tournament-embed-header d-flex justify-end align-end px-0">
          <LanguageSelector :allowed-languages="embedConfig.allowedLangs"></LanguageSelector>
        </v-col>
        <div class="d-none d-sm-block">
          <span :class="['status-chip', tournamentStatusCssClass]" :style="statusChipStyle">
            {{ tournamentStatusLabel }}
          </span>
          <div class="d-flex align-center">
            <h1 class="tournament-title mt-4">{{ tournamentName }}</h1>
          </div>
          <h3 class="tournament-organizer">{{ $t('organizedBy') }} {{ tournamentOrganizer }}</h3>
          <div v-if="showEndedTimerLabel" class="tournament-ended-label-overview">
            <TournamentEndedLabel
              :tournament-end-date="new Date(tournamentData.endTime)"
            />
          </div>
        </div>

        <!-- Enhanced Mobile header layout -->
        <div class="mobile-tournament-header d-block d-sm-none">
          <!-- Tournament title with visual enhancement -->
          <div class="mobile-tournament-title-container">
            <!-- Right-positioned status indicator (just the blinking circle) -->
            <div v-if="false" :class="['status-indicator-right', tournamentStatusCssClass]">
              <span class="blink-circle"></span>
              <!-- Decorative splatter element -->
              <span class="splatter-effect"></span>
            </div>

            <h1 class="mobile-tournament-title">{{ tournamentName }}</h1>
            <div class="mobile-title-underline"></div>
          </div>

          <!-- Organizer info -->
          <h3 class="mobile-tournament-organizer">
            {{ $t('organizedBy') }} <span class="organizer-name">{{ tournamentOrganizer }}</span>
          </h3>
        </div>

        <!-- Tournament Timer - positioned better for both desktop and mobile -->
        <v-col
          v-if="shouldShowTimer"
          cols="12"
          class="tournament-timer-container"
          :class="{ 'd-block d-sm-none': showEndedTimerLabel }"
        >
          <TournamentTimer
            :tournamentStartDate="new Date(tournamentData.startTime)"
            :tournamentEndDate="new Date(tournamentData.endTime)"
            :tournamentInProgress="tournamentStatus === 'IN_PROGRESS'"
            :tournamentIsActive="tournamentStatus === 'STILL_OPEN' || tournamentStatus === 'IN_PROGRESS'"
            :tournamentUserEnrolled="participantData"
          />
        </v-col>

      </v-col>

      <!-- View for enrolled users (with identifier) -->
      <div v-if="hasIdentifier" class="enrolled-user-view">
        <v-row>
          <!-- Tournament Timer -->
          <v-col cols="12" lg="4" md="4" sm="12" xs="12" class="flex-column show-on-mobile">
            <div class="mobile-leaderboard-header d-block d-sm-none" style="margin-bottom: 15px;">
              <div class="leaderboard-title-container">
                <i class="fa-regular fa-user mr-2"></i>
                <h2>{{ $t('yourParticipation') }}</h2>
              </div>
            </div>
            <!-- User Summary -->
            <UserSummary :canChangeAcc="false" :participants="tournamentData.participants"
              :participant-data="participantData" :ranking-type="tournamentData.tournamentSettings.tournamentRankingType" />
          </v-col>
          <!-- Two-column layout for enrolled user view -->
            <v-col cols="12" lg="8" md="8" sm="12" xs="12">
              <!-- Tournament Brackets -->
              <TournamentBrackets
                v-if="tournamentData && tournamentData.tournamentId"
                :tournament-id="tournamentData.tournamentId"
                @bracket-selected="handleBracketChange" />

              <Table :items="displayParticipants" :expandable="tournamentData?.tournamentSettings?.publicPerformance === true" :blurred="antiSpoiler"
              :serverPagination="true"
              :serverTotalPages="serverTotalPages"
              :serverCurrentPage="serverCurrentPage"
              :serverTotalElements="serverTotalElements"
              :participantsLoading="participantsLoading"
              @page-change="$emit('page-change', $event)"
              @search-change="$emit('search-change', $event)" />
            </v-col>

            <v-col cols="12" lg="4" md="4" sm="12" xs="12" class="d-flex flex-column">
              <!-- Tournament Details Header - Mobile Only -->
              <div class="mobile-leaderboard-header d-block d-sm-none">
                <div class="leaderboard-title-container">
                  <i class="fa-regular fa-trophy mr-2"></i>
                  <h2>{{ $t('tournamentDetails') }}</h2>
                </div>
              </div>

              <!-- User Summary -->
              <UserSummary class="hide-on-mobile" :canChangeAcc="false" :participants="tournamentData.participants"
                :participant-data="participantData" :ranking-type="tournamentData.tournamentSettings.tournamentRankingType"
                :tournament-closed="tournamentClosed" />

              <!-- Tournament Awards -->
              <div class="mt-5">
                <TournamentAwards :should-show-awards="shouldShowAwards" :should-show-sponsor="shouldShowSponsor"/>
              </div>

              <!-- Tournament Prize Pool -->
              <div class="mt-5">
                <TournamentPrizePool />
              </div>

              <!-- Desktop Action Buttons - Moved here below the cards -->
              <div class="desktop-embed-footer d-none d-sm-flex mt-4">
                <button v-if="shouldShowRules" class="desktop-action-btn" @click="showRulesModal = true"
                  :style="faqButtonStyle">
                  <i class="fa-regular fa-list mr-2"></i>
                  {{ $t('viewRules_TournamentEmbedView') }}
                </button>
                <button v-if="shouldShowFaqs && tournamentData.tournamentSettings.faqsUrl" class="desktop-action-btn ml-3"
                  @click="openFaqsUrl" :style="faqButtonStyle">
                  <i class="fa-regular fa-circle-question mr-2"></i>
                  {{ $t('viewFAQs_TournamentEmbedView') }}
                </button>
              </div>
            </v-col>
        </v-row>
      </div>

      <!-- View for public (no identifier) -->
      <div v-else>
        <!-- Registration CTA Banner (Responsive) with sticky scroll behavior -->
        <div v-if="showRegistrationCTA" class="registration-banner mb-4" :class="{ 'hidden': hideBannerOnMobile }"
          :style="bannerStyle">
          <!-- Desktop version -->
          <div class="banner-content d-none d-sm-flex align-center justify-space-between">
            <div>
              <h3 class="mb-0 banner-title"  :style="bannerTextStyle">{{ $t('joinTournamentDescription') }} {{ registrationCloseDateText }} {{ $t('joinTournamentDescriptionTwo') }}</h3>
              <h3 class="textStyle" :style="bannerTextStyle">{{ registrationCountdownText }}</h3>
            </div>
            <v-btn :style="registerButtonStyle" class="px-5" @click="openRegisterModal">
              {{ $t('registerNow') }}
              <i class="fa-regular fa-user-plus mr-2 register-desktop-icon"></i>
            </v-btn>
          </div>

          <!-- Mobile version -->
          <div class="banner-content-mobile d-flex d-sm-none flex-column align-center">
            <h4 class="text-center mb-3 banner-title" :style="bannerTextStyle">{{ $t('joinTournamentDescription') }} {{ registrationCloseDateText }} {{ $t('joinTournamentDescriptionTwo') }}</h4>
            <h4 class="textStyle" :style="bannerTextStyle">{{ registrationCountdownText }}</h4>
            <v-btn :style="registerButtonStyle" class="px-5 register-mobile-btn" @click="openRegisterModal" block>
              <i class="fa-regular fa-user-plus mr-2"></i>
              {{ $t('registerNow') }}
            </v-btn>
          </div>
        </div>

        <!-- Tournament Awards for Public View -->
        <v-row class="mx-0">
          <v-col cols="12" :lg="shouldShowRightColumn ? 8 : 12" :md="shouldShowRightColumn ? 8 : 12" sm="12" xs="12">
            <!-- Tournament Brackets -->
            <TournamentBrackets
              v-if="tournamentData && tournamentData.tournamentId"
              :tournament-id="tournamentData.tournamentId"
              @bracket-selected="handleBracketChange" />

            <!-- Leaderboard -->
            <Table :items="displayParticipants" :expandable="tournamentData?.tournamentSettings?.publicPerformance === true" :blurred="antiSpoiler"
              :serverPagination="true"
              :serverTotalPages="serverTotalPages"
              :serverCurrentPage="serverCurrentPage"
              :serverTotalElements="serverTotalElements"
              :participantsLoading="participantsLoading"
              @page-change="$emit('page-change', $event)"
              @search-change="$emit('search-change', $event)" />
          </v-col>
          <v-col v-if="shouldShowRightColumn"  cols="12" lg="4" md="4" sm="12" xs="12" class="d-flex flex-column">
            <!-- User Summary -->
            <UserSummary class="hide-on-mobile" :canChangeAcc="false" :participants="tournamentData.participants"
              :participant-data="participantData" :ranking-type="tournamentData.tournamentSettings.tournamentRankingType"
              :tournament-closed="tournamentClosed" />

            <!-- Tournament Details Header - Mobile Only -->
            <div class="mobile-leaderboard-header d-block d-sm-none">
              <div class="leaderboard-title-container">
                <i class="fa-regular fa-trophy mr-2"></i>
                <h2>{{ $t('tournamentDetails') }}</h2>
              </div>
            </div>

            <!-- Tournament Awards -->
            <div class="mt-5">
              <TournamentAwards :should-show-awards="shouldShowAwards" :should-show-sponsor="shouldShowSponsor" />
            </div>

            <!-- Tournament Prize Pool -->
            <div class="mt-5">
              <TournamentPrizePool />
            </div>

            <!-- Desktop Action Buttons - Moved here below the cards -->
            <div class="desktop-embed-footer d-none d-sm-flex mt-4">
              <button v-if="shouldShowRules" class="desktop-action-btn" @click="showRulesModal = true"
                :style="faqButtonStyle">
                <i class="fa-regular fa-list mr-2"></i>
                {{ $t('viewRules_TournamentEmbedView') }}
              </button>
              <button v-if="shouldShowFaqs && tournamentData.tournamentSettings.faqsUrl" class="desktop-action-btn ml-3"
                @click="openFaqsUrl" :style="faqButtonStyle">
                <i class="fa-regular fa-circle-question mr-2"></i>
                {{ $t('viewFAQs_TournamentEmbedView') }}
              </button>
            </div>
          </v-col>
        </v-row>
      </div>

      <!-- Static Mobile Footer with Rules and FAQs (non-fixed) -->
      <div class="mobile-embed-footer d-block d-sm-none">
        <div class="footer-actions">
          <button v-if="shouldShowRules" class="footer-action-btn" @click="showRulesModal = true"
            :style="faqButtonStyle">
            <i class="fa-regular fa-list mr-2"></i>
            {{ $t('viewRules_TournamentEmbedView') }}
          </button>
          <button v-if="shouldShowFaqs && tournamentData.tournamentSettings.faqsUrl" class="footer-action-btn" @click="openFaqsUrl"
            :style="faqButtonStyle">
            <i class="fa-regular fa-circle-question mr-2"></i>
            {{ $t('viewFAQs_TournamentEmbedView') }}
          </button>
        </div>
      </div>

      <!-- Footer for all views -->
      <!--<div v-if="showFooter" class="tournament-embed-footer">
        <div class="powered-by ml-auto">
          {{ $t('poweredBy') }} <a href="https://swiset.com" target="_blank">Swiset</a>
        </div>
      </div>-->
    </template>

    <!-- All modals/dialogs remain the same regardless of view mode -->
    <!-- Registration Modal -->
    <v-dialog v-model="showRegisterModal" max-width="500px" persistent>
      <PublicTournamentRegister
        :tournament-id="tournamentData.tournamentId"
        :tournament-data="tournamentData"
        @close="showRegisterModal = false"
        @registered="handleRegistrationSuccess" />
    </v-dialog>

    <!-- Auto-popup registration modal -->
    <v-dialog  v-if="tournamentShowRegister && !registrationExpired" v-model="autoShowRegisterModal" max-width="500px" persistent>
      <PublicTournamentRegister
        :tournament-id="tournamentData.tournamentId"
        :tournament-data="tournamentData"
        @close="autoShowRegisterModal = false"
        @registered="handleRegistrationSuccess" />
    </v-dialog> 

    <!-- Improved Tournament Rules Modal -->
    <v-dialog v-model="showRulesModal" max-width="450px" content-class="rules-dialog-container" scrollable
      overlay-opacity="0.8" transition="dialog-bottom-transition">
      <RulesDialog :rules="formattedRulesHtml" @close="showRulesModal = false" />
    </v-dialog>
  </div>
</template>

<script>
import PublicTournamentRegister from "./PublicTournamentRegister.vue";
import Table from "../../../components/tournament-pos-table/Table.vue";
import TournamentTimer from "@/components/TournamentTimer";
import TournamentEndedLabel from "@/components/TournamentEndedLabel.vue";
import UserSummary from "@/views/Tournament/components/UserSummary";
import TournamentAwards from "@/views/Tournament/components/TournamentAwards";
import TournamentPrizePool from "@/views/Tournament/components/TournamentPrizePool";
import TournamentBrackets from "@/views/Tournament/components/TournamentBrackets";
import RulesDialog from "@/views/Tournament/components/RulesDialog.vue";
import Repository from '../../../repositories/tournament';
import LanguageSelector from "../../../components/LanguageSelector.vue";
import { setLocale, i18nEventBus } from "@/i18n"; // Updated import
import { escapeHtml } from "@/utils/safeHtml";

export default {
  name: 'TournamentEmbedView',
  components: {
    Table,
    PublicTournamentRegister,
    TournamentTimer,
    TournamentEndedLabel,
    UserSummary,
    TournamentAwards,
    TournamentPrizePool,
    RulesDialog,
    LanguageSelector,
    TournamentBrackets
  },
  props: {
    tournamentData: {
      type: Object,
      required: true
    },
    showFooter: {
      type: Boolean,
      default: true
    },
    tournamentRules: {
      type: Array,
      default: () => []
    },
    serverTotalPages: {
      type: Number,
      default: 1,
    },
    serverCurrentPage: {
      type: Number,
      default: 1,
    },
    serverTotalElements: {
      type: Number,
      default: 0,
    },
    participantsLoading: {
      type: Boolean,
      default: false,
    },
    tournamentClosed: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showRegisterModal: false,
      autoShowRegisterModal: false,
      userRegistered: false,
      identifier: this.$route.query.identifier || null,
      userInteracted: false,
      autoPopupTimeout: null,
      hideBannerOnMobile: false,
      scrollTimeout: null,
      isRedirecting: false,
      showRulesModal: false,
      currentLocale: null, // Add this new property to track language changes
      refreshKey: 0, // Add this to force component re-render
      countdownInterval: null,
      tournamentScheduleInterval: null,
      now: new Date(),
      // New embed configuration from URL parameters
      embedConfig: {
        theme: this.$route.query.theme || 'light',
        lang: this.$route.query.lang || null,
        allowedLangs: this.$route.query.allowed_langs ? this.$route.query.allowed_langs.split(',') : null
      },
      // Tournament IDs that manage their own registration (no popup/CTA needed)
      tournamentsWithExternalRegistration: [376, 474, 506, 515, 509, 510, 511, 529],

      tournamentBranding: {
        light: null,
        dark: null
      }
    };
  },
  watch: {
    // Watch for route changes to update embed configuration
    '$route.query': {
      handler(newQuery) {
        // Update embed configuration when URL parameters change
        this.embedConfig = {
          theme: newQuery.theme || 'light',
          lang: newQuery.lang || null,
          allowedLangs: newQuery.allowed_langs ? newQuery.allowed_langs.split(',') : null
        };
        this.identifier = newQuery.identifier || null;
        // Re-apply the configuration
        this.$nextTick(() => {
          this.initializeEmbedConfiguration();
        });
      },
      deep: true,
      immediate: false
    },
    'tournamentData.tournamentId': {
        handler(newTournamentId) {
          if (newTournamentId) {
            this.fetchTournamentBranding();
          }
        },
        immediate: true
    },
    activeBrandingTheme: {
      handler() {
        this.emitBackgroundColor();
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    // New computed property to check if we should show the minimal view
    showMinimalView() {
      const selectedFeatures = this.tournamentData?.selectedFeatures;
      if (!selectedFeatures || !Array.isArray(selectedFeatures)) {
        return false;
      }

      const hasNameFeature = selectedFeatures.includes('TOURNAMENT_RANKING_NAME');
      const hasUsernameFeature = selectedFeatures.includes('TOURNAMENT_RANKING_USERNAME');

      return !hasNameFeature && !hasUsernameFeature;
    },
    hasIdentifier() {
      return !!this.identifier;
    },
    participants() {
      return this.tournamentData.participants.filter(p => p.status === 'ACTIVE') || [];
    },
    displayParticipants() {
      // If there are already participants, just return them
      if (this.participants.length > 0) {
        return this.participants;
      }

      // Otherwise, create 3 mock participants
      const mockParticipants = [];
      for (let i = 0; i < 3; i++) {
        mockParticipants.push({
          profilePicture: null,
          firstName: '---',
          lastName: '---',
          username: '---',
          score: null,
          previousPlace: null,
          country: null,
          status: 'ACTIVE'
        });
      }

      return mockParticipants;
    },
    participantData() {
      if (!this.hasIdentifier) return null;
      return this.tournamentData.participants.find(
        participant => participant.brokerAccountIdentifier === this.identifier
      ) || null;
    },
    tournamentName() {
      return this.tournamentData.tournamentName || 'Tournament';
    },
    tournamentShowRegister() {
      return this.tournamentData?.selectedFeatures?.includes("TOURNAMENT_RANKING_SHOW_REGISTER") && this.tournamentStatus !== 'CLOSED'  || false;
    },
    tournamentOrganizer() {
      return this.tournamentData.tournamentOrganizer || 'Swiset';
    },
    tournamentStatus() {
      const now = new Date();
      const startTime = new Date(this.tournamentData.startTime);
      const endTime = new Date(this.tournamentData.endTime);

      if (now < startTime) {
        return 'STILL_OPEN';
      }
      if (now >= startTime && now <= endTime) {
        return 'IN_PROGRESS';
      }
      return 'CLOSED';
    },
    tournamentStatusCssClass() {
      const map = {
        STILL_OPEN: 'still-open',
        IN_PROGRESS: 'in-progress',
        CLOSED: 'closed'
      };
      return map[this.tournamentStatus] || '';
    },
    tournamentStatusLabel() {
      const keyMap = {
        STILL_OPEN: 'tournamentStatusStillOpen_TournamentEmbedView',
        IN_PROGRESS: 'tournamentStatusInProgress_TournamentEmbedView',
        CLOSED: 'tournamentStatusClosed_TournamentEmbedView'
      };
      return this.$t(keyMap[this.tournamentStatus]);
    },
    antiSpoiler() {
      return this.tournamentData.antiSpoiler || false;
    },
    fullTournamentUrl() {
      const baseUrl = `${window.location.origin}/tournaments/rank?tournamentId=${this.tournamentData.tournamentId}`;
      return this.hasIdentifier ? `${baseUrl}&identifier=${this.identifier}` : baseUrl;
    },
    // Show registration CTA only if tournament hasn't ended, user isn't registered, and no identifier
    // Don't show registration CTA in minimal view
    showRegistrationCTA() {
      if (!this.tournamentShowRegister ) {
        return false;
      }

      if (!this.registrationCloseTime) {
        return false;
      }

      return !this.hasIdentifier &&
        !this.userRegistered &&
        !this.registrationExpired &&
        this.tournamentStatus !== 'CLOSED';
    },
    formattedRules() {
      if (!this.tournamentRules || !this.tournamentRules.length) {
        return [];
      }

      // Return properly formatted rules ensuring all rules have a description
      return this.tournamentRules.map(rule => {
        // Make sure each rule has a proper description
        if (!rule.description || rule.description === '') {
          // If this is the MAX_TRADE_SIZE rule without a description
          if (rule.ruleId === 'MAX_TRADE_SIZE') {
            return {
              ...rule,
              description: this.$t('maxTradeSize')
            };
          }
          // Generic fallback for any rule missing a description
          return {
            ...rule,
            description: rule.ruleId || this.$t('unknownRule')
          };
        }
        return rule;
      });
    },
    formattedRulesHtml() {
      if (!this.tournamentRules || !this.tournamentRules.length) {
        return null;
      }

      // Convert tournament rules array to HTML string for RulesDialog component
      let rulesHtml = `<p class="mb-5">${this.$t('rulesDescription')}</p><ul class="rules-list">`;

      this.formattedRules.forEach(rule => {
        rulesHtml += `<li class="rule-item">
          <strong>${escapeHtml(rule.description)}</strong>: ${escapeHtml(rule.value)}
        </li>`;
      });

      rulesHtml += '</ul>';
      return rulesHtml;
    },
    // Add organizer color style computed property
    organizerColorStyle() {
      const colors = this.tournamentData?.tournamentSettings?.colors;
      if (colors && (colors.primary || colors.secondary)) {
        const isDark = this.$vuetify.theme.dark;
        const color = isDark && colors.secondary ? colors.secondary : colors.primary;
        if (!color) return {};
        const cleanColor = color.replace('#', '');
        const fullColor = `#${cleanColor}`;
        return {
          backgroundColor: fullColor,
          borderColor: fullColor,
          color: this.getContrastYIQ(cleanColor) === 'black' ? '#000 !important' : '#fff!important'
        };
      }
      return {};
    },
    // Add new computed properties for banner styling
    hasOrganizerColor() {
      const colors = this.tournamentData?.tournamentSettings?.colors;
      return colors && (colors.primary || colors.secondary);
    },
    _activeOrganizerColor() {
      const colors = this.tournamentData?.tournamentSettings?.colors;
      if (!colors) return null;
      const isDark = this.$vuetify.theme.dark;
      return isDark && colors.secondary ? colors.secondary : colors.primary;
    },
    bannerStyle() {
      const primary = this.activeBrandingTheme?.brandPrimary;

      if (primary) {
        const cleanPrimary = primary.replace('#', '');
        return {
          backgroundColor: `#${cleanPrimary}`,
          borderColor: `#${cleanPrimary}`
        };
      }

      if (this.hasOrganizerColor && this._activeOrganizerColor) {
        const cleanColor = this._activeOrganizerColor.replace('#', '');
        return {
          backgroundColor: `#${cleanColor}`,
          borderColor: `#${cleanColor}`
        };
      }

      return {
        backgroundColor: '#F9E65F',
        borderColor: 'rgba(0, 0, 0, 0.08)'
      };
    },
    bannerTextStyle() {
      const primary = this.activeBrandingTheme?.brandPrimary;
      const secondary = this.activeBrandingTheme?.brandSecondary;

      if (primary || secondary) {
        const textColor = secondary
          ? `#${secondary.replace('#', '')}`
          : (this.getContrastYIQ(primary.replace('#', '')) === 'black' ? '#000000' : '#FFFFFF');

        return `color: ${textColor} !important;`;
      }

      if (this.hasOrganizerColor && this._activeOrganizerColor) {
        const cleanColor = this._activeOrganizerColor.replace('#', '');
        const textColor = this.getContrastYIQ(cleanColor) === 'black' ? '#000000' : '#FFFFFF';

        return `color: ${textColor} !important;`;
      }

      return 'color: #2c3e50 !important;';
    },
    buttonColor() {
      if (this.hasOrganizerColor) {
        return ''; // Empty string to remove v-btn color
      }
      return 'mainGreen';
    },

    registerButtonStyle() {
      const primary = this.activeBrandingTheme?.brandPrimary;
      const secondary = this.activeBrandingTheme?.brandSecondary;

      if (primary || secondary) {
        const backgroundColor = secondary
          ? `#${secondary.replace('#', '')}`
          : '#000000';

        const textColor = primary
          ? `#${primary.replace('#', '')}`
          : '#FFFFFF';

        return `
          background-color: ${backgroundColor} !important;
          color: ${textColor} !important;
        `;
      }

      if (this.hasOrganizerColor && this._activeOrganizerColor) {
        const cleanColor = this._activeOrganizerColor.replace('#', '');
        const textColor = `#${cleanColor}`;
        const backgroundColor = this.getContrastYIQ(cleanColor) === 'black' ? '#FFFFFF' : '#000000';

        return `
          background-color: ${backgroundColor} !important;
          color: ${textColor} !important;
        `;
      }

      return `
        background-color: #2c3e50 !important;
        color: #FFFFFF !important;
      `;
    },
    shouldShowTimer() {
      const selectedFeatures = this.tournamentData?.selectedFeatures;
      // If selectedFeatures doesn't exist (old tournaments), show all features
      if (!selectedFeatures || !Array.isArray(selectedFeatures)) {
        return true;
      }
      return selectedFeatures.includes('TOURNAMENT_RANKING_COUNTER');
    },
    showEndedTimerLabel() {
      const endTime = this.tournamentData?.endTime;
      if (!endTime) return false;
      return new Date(endTime) < new Date();
    },
    shouldShowRules() {
      const selectedFeatures = this.tournamentData?.selectedFeatures;
      // If selectedFeatures doesn't exist (old tournaments), show all features
      if (!selectedFeatures || !Array.isArray(selectedFeatures)) {
        return true;
      }
      return selectedFeatures.includes('TOURNAMENT_RANKING_RULES');
    },
    shouldShowFaqs() {
      const selectedFeatures = this.tournamentData?.selectedFeatures;
      // If selectedFeatures doesn't exist (old tournaments), show all features
      if (!selectedFeatures || !Array.isArray(selectedFeatures)) {
        return true;
      }
      return selectedFeatures.includes('TOURNAMENT_RANKING_FAQS');
    },
    
    // Computed property for status chip styling with YIQ contrast
    statusChipStyle() {
      const status = this.tournamentStatusCssClass;
      let backgroundColor = '';
      
      switch (status) {
        case 'still-open':
          backgroundColor = 'FFF75F';
          break;
        case 'in-progress':
          // Use the green color value - matches your CSS variable
          backgroundColor = '00F784';
          break;
        case 'closed':
          backgroundColor = '111111';
          break;
        case 'archived':
          backgroundColor = '6c757d';
          break;
        default:
          backgroundColor = 'f8f8f8';
      }
      
      const textColor = this.getContrastYIQ(backgroundColor);
      
      return {
        color: textColor === 'black' ? '#000000' : '#ffffff'
      };
    },

    isDarkTheme() {
      return this.embedConfig.theme === 'dark';
    },

    activeBrandingTheme() {
      return this.isDarkTheme
        ? this.tournamentBranding?.dark
        : this.tournamentBranding?.light;
    },

    faqButtonStyle() {
      const primary = this.activeBrandingTheme?.brandPrimary;
      const secondary = this.activeBrandingTheme?.brandSecondary;

      if (primary || secondary) {
        return {
          backgroundColor: primary ? `#${primary.replace('#', '')}` : '',
          borderColor: primary ? `#${primary.replace('#', '')}` : '',
          color: secondary ? `#${secondary.replace('#', '')}` : ''
        };
      }

      // fallback al estilo anterior
      return this.organizerColorStyle;
    },
    backgroundStyle() {
      const background = this.activeBrandingTheme?.background;
      if (background) {
        return {
          backgroundColor: `#${background.replace('#', '')}`,
        };
      }
      return {};
    },

    registrationCloseTime() {
      const startTime = this.tournamentData?.startTime;
      const daysPostRegister = this.tournamentData?.tournamentSettings?.daysPostRegister;
      if (!startTime || daysPostRegister === null || daysPostRegister === undefined) {
        return null;
      }
      const closeDate = new Date(startTime);
      closeDate.setDate(closeDate.getDate() + Number(daysPostRegister));
      return closeDate;
    },

    registrationCountdown() {
      if (!this.registrationCloseTime) {
        return null;
      }

      const diff = this.registrationCloseTime.getTime() - this.now.getTime();

      if (diff <= 0) {
        return {
          days: 0,
          hours: 0,
          mins: 0,
          secs: 0,
          expired: true
        };
      }

      const totalSeconds = Math.floor(diff / 1000);
      const days = Math.floor(totalSeconds / (60 * 60 * 24));
      const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
      const mins = Math.floor((totalSeconds % (60 * 60)) / 60);
      const secs = totalSeconds % 60;

      return {
        days,
        hours,
        mins,
        secs,
        expired: false
      };
    },

    registrationCountdownText() {
      if (!this.registrationCountdown) {
        return '';
      }

      if (this.registrationCountdown.expired) {
        return this.$t('registrationClosed');
      }

      const { days, hours, mins, secs } = this.registrationCountdown;
      const parts = [];

      const formatUnit = (value, key) => {
        return `${value} ${this.$tc(key, value)}`;
      };

      if (days > 0) {
        parts.push(formatUnit(days, 'nowDay'));
      }

      if (hours > 0) {
        parts.push(formatUnit(hours, 'nowHour'));
      }

      if (mins > 0) {
        parts.push(formatUnit(mins, 'nowMinutes'));
      }

      parts.push(formatUnit(secs, 'nowSeconds'));

      return `${this.$t('registrationCloses')} ${parts.join(', ')}`;
    },

    registrationCloseDateText() {
      if (!this.registrationCloseTime) {
        return '';
      }

      const date = this.registrationCloseTime;
      const day = date.getDate();

      const getOrdinalSuffix = (day) => {
        if (day >= 11 && day <= 13) {
          return 'th';
        }

        switch (day % 10) {
          case 1:
            return 'st';
          case 2:
            return 'nd';  
          case 3:
            return 'rd';
          default:
            return 'th';
        }
      };

      const months = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
      ];

      return `${day}${getOrdinalSuffix(day)} ${months[date.getMonth()]}`;
    },
    registrationExpired() {
      if (!this.registrationCloseTime) {
        return false;
      }

      return this.now >= this.registrationCloseTime;
    },
    shouldShowAwards() {
      const selectedFeatures = this.$store.state.tournament?.selectedFeatures;
      if (!selectedFeatures || !Array.isArray(selectedFeatures)) {
        return true;
      }
      return selectedFeatures.includes('TOURNAMENT_RANKING_AWARD');
    },
    shouldShowSponsor() {
      const selectedFeatures = this.$store.state.tournament?.selectedFeatures;
      if (!selectedFeatures || !Array.isArray(selectedFeatures)) return true;
      return selectedFeatures.includes("TOURNAMENT_RANKING_SPONSORED");
    },
    shouldShowRightColumn() {
    return ( this.shouldShowRules || this.shouldShowFaqs || this.shouldShowAwards || this.shouldShowSponsor
    );
  }
 
  },
  mounted() {
    // Initialize embed configuration (theme and language)
    this.initializeEmbedConfiguration();
    
    // Set language based on tournament organizer ID and reload if needed
    this.setLanguageBasedOnTournament();

    // Listen for language change events
    i18nEventBus.$on('language-changed', () => {
      // Force re-render of the entire component tree by changing the key
      this.refreshKey += 1;
    });

    // Skip auto-popup registration modal in minimal view or for tournaments with external registration
    const shouldSkipAutoPopup = !this.tournamentShowRegister || this.registrationExpired;

    if (!shouldSkipAutoPopup && !this.hasIdentifier && this.tournamentStatus !== 'CLOSED') {
      this.autoPopupTimeout = setTimeout(() => {
        if (!this.userRegistered && !this.userInteracted && !this.registrationExpired) {
          this.autoShowRegisterModal = true;
        }
      }, 30000);
    }

    // Add scroll event listener for mobile banner behavior but only if not in minimal view
    if (this.showRegistrationCTA) {
      window.addEventListener('scroll', this.handleScroll);
    }
    this.startRegistrationCountdown();
    this.startTournamentScheduleWatcher();
  },
  beforeDestroy() {
    // Clear timeout when component is destroyed
    if (this.autoPopupTimeout) {
      clearTimeout(this.autoPopupTimeout);
    }

    // Remove event listener
    window.removeEventListener('scroll', this.handleScroll);

    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    if (this.tournamentScheduleInterval) {
      clearInterval(this.tournamentScheduleInterval);
    }

    // Remove the event listener
    i18nEventBus.$off('language-changed');
  },
  methods: {
    // New methods for embed configuration
    initializeEmbedConfiguration() {
      // Apply theme configuration
      this.applyTheme();
      
      // Apply language configuration
      this.applyLanguageConfiguration();
    },

    applyTheme() {
      // Use Vuetify's built-in theme system and always force the theme based on URL parameter
      if (this.$vuetify && this.$vuetify.theme) {
        const shouldBeDark = this.embedConfig.theme === 'dark';
        
        // Always apply the theme from URL parameter, regardless of current state
        this.$vuetify.theme.dark = shouldBeDark;
        
        // Also force update by using $nextTick to ensure it's applied
        this.$nextTick(() => {
          this.$vuetify.theme.dark = shouldBeDark;
        });
      }
    },

    applyLanguageConfiguration() {
      // If a specific language is requested and it's valid, set it
      if (this.embedConfig.lang && this.isValidLanguage(this.embedConfig.lang)) {
        // Check if the requested language is allowed
        if (!this.embedConfig.allowedLangs || this.embedConfig.allowedLangs.includes(this.embedConfig.lang)) {
          // Only set the language if it's different from current
          if (this.$i18n.locale !== this.embedConfig.lang) {
            this.$i18n.locale = this.embedConfig.lang;
            this.$store.commit('setPreferredLanguage', this.embedConfig.lang);
          }
        }
      }
    },

    isValidLanguage(langCode) {
      const validLanguages = ['en', 'es', 'ko', 'th', 'pt', 'zh', 'zh-Hant', 'vi', 'ja'];
      return validLanguages.includes(langCode);
    },

    emitBackgroundColor() {
      const background = this.activeBrandingTheme?.background;
      this.$emit(
        'background-color-change',
        background ? `#${background.replace('#', '')}` : ''
      );
    },

    openRegisterModal() {
      if (!this.tournamentShowRegister || this.registrationExpired) {
        return;
      }

      this.userInteracted = true;

      if (this.autoPopupTimeout) {
        clearTimeout(this.autoPopupTimeout);
        this.autoPopupTimeout = null;
      }

      this.showRegisterModal = true;
    },
    async handleRegistrationSuccess(payload) {
      this.userRegistered = true;
      this.userInteracted = true;
      this.showRegisterModal = false;
      this.autoShowRegisterModal = false;

      // Clear the auto-popup timeout if it exists
      if (this.autoPopupTimeout) {
        clearTimeout(this.autoPopupTimeout);
        this.autoPopupTimeout = null;
      }
      const identifier = payload?.identifier;

      const externalId = this.tournamentData?.tournamentSettings?.externalId;

      if (identifier && externalId) {
        this.isRedirecting = true;
        const newUrl = `${window.location.origin}${this.$route.path}?tournamentId=${externalId}&identifier=${identifier}`;
        setTimeout(() => {
          window.location.replace(newUrl);
        }, 50);
      }
    },
    
    openFullTournament() {
      window.open(this.fullTournamentUrl, '_blank');
    },
    handleScroll() {
      // Clear previous timeout to avoid rapid changes
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }

      // Set a short timeout to avoid performance issues
      this.scrollTimeout = setTimeout(() => {
        // Calculate how close to bottom we are
        const scrollPosition = window.scrollY + window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        const distanceToBottom = documentHeight - scrollPosition;

        // If we're near bottom (within 150px) or at bottom, hide the banner
        this.hideBannerOnMobile = distanceToBottom < 150;
      }, 100);
    },
    openFaqsUrl() {
      if (this.tournamentData.tournamentSettings.faqsUrl) {
        window.open(this.tournamentData.tournamentSettings.faqsUrl, '_blank');
      }
    },
    async handleBracketChange(bracket) {
      // When bracket changes, fetch the tournament overview for the selected bracket's tournament
      try {
        const response = await Repository.getTournamentOverviewOpen(bracket.externalId);

        if (response.status === 200) {
          const tournamentData = response.data;

          // For brackets, use master tournament info from the bracket data
          const masterName = bracket.masterName;
          const masterStartTime = bracket.tournamentStartTime;
          const masterEndTime = bracket.tournamentEndTime;
          const originalStatus = this.tournamentData.tournamentStatus;
          const originalOrganizer = this.tournamentData.tournamentOrganizer || this.tournamentData.creator?.name;
          const originalImageUrl = this.tournamentData.imageUrl;

          // Emit event to parent component to update tournament data
          this.$emit('update-tournament-data', {
            ...tournamentData.tournamentSettings.tournament,
            tournamentId: bracket.tournamentId,
            tournamentName: masterName, // Use master tournament name
            name: masterName, // Use master tournament name
            startTime: masterStartTime, // Use master start time
            endTime: masterEndTime, // Use master end time
            tournamentStatus: originalStatus,
            tournamentOrganizer: originalOrganizer,
            imageUrl: originalImageUrl,
            tournamentSettings: tournamentData.tournamentSettings,
            awards: tournamentData.awards,
            participants: tournamentData.participants,
            selectedFeatures: tournamentData.selectedFeatures
          });
        }
      } catch (error) {
        console.error('Error fetching bracket tournament data:', error);
      }
    },
    // Helper method to determine if text should be black or white based on background color
    getContrastYIQ(hexcolor) {
      if (!hexcolor) return 'black';

      // Remove hash if it exists
      hexcolor = hexcolor.replace('#', '');

      const r = parseInt(hexcolor.substr(0, 2), 16);
      const g = parseInt(hexcolor.substr(2, 2), 16);
      const b = parseInt(hexcolor.substr(4, 2), 16);

      const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;

      return (yiq >= 128) ? 'black' : 'white';
    },
    setLanguageBasedOnTournament() {
      const tournamentId = this.tournamentData?.tournamentId;
      const targetLocale = this.tournamentData?.tournamentSettings?.mainLanguage;
      if (!tournamentId || !targetLocale) {
        return;
      }

      const languageSetKey = `language_set_for_tournament_${tournamentId}`;

      if (localStorage.getItem(languageSetKey) === 'true') {
        return;
      }

      if (localStorage.getItem(`user_changed_locale_tournament_${tournamentId}`)) {
        localStorage.setItem(languageSetKey, 'true');
        return;
      }

      if (this.$i18n.locale === targetLocale) {
        localStorage.setItem(languageSetKey, 'true');
        return;
      }

      localStorage.setItem(languageSetKey, 'true');
      setLocale(targetLocale);
      this.$store.commit('setPreferredLanguage', targetLocale);

      setTimeout(() => {
        window.location.reload();
      }, 100);
    },
    async fetchTournamentBranding() {
      try {
        const tournamentId = this.tournamentData?.tournamentId;
        if (!tournamentId) return;

        const res = await Repository.getTournamentBranding(tournamentId);
        const branding = res?.data || {};

        this.tournamentBranding = {
          light: branding.light || null,
          dark: branding.dark || null
        };
      } catch (e) {
        console.warn('Failed to load tournament branding:', e);
        this.tournamentBranding = {
          light: null,
          dark: null
        };
      }
      this.$nextTick(() => {
      this.emitBackgroundColor();
    });
    },
    startRegistrationCountdown() {
      this.now = new Date();

      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
      }

      this.countdownInterval = setInterval(() => {
        this.now = new Date();
      }, 1000);
    },
    startTournamentScheduleWatcher() {
      if (this.tournamentScheduleInterval) {
        clearInterval(this.tournamentScheduleInterval);
      }

      let watchedBeforeStart = false;
      let watchedBeforeEnd = false;

      const tick = () => {
        const startTime = this.tournamentData?.startTime;
        const endTime = this.tournamentData?.endTime;
        if (!startTime || !endTime) return;

        const now = Date.now();
        const startMs = new Date(startTime).getTime();
        const endMs = new Date(endTime).getTime();
        const toStart = startMs - now;
        const toEnd = endMs - now;

        if (toStart > 0) watchedBeforeStart = true;
        if (now >= startMs && toEnd > 0) watchedBeforeEnd = true;

        if (watchedBeforeStart && toStart <= 0 && now < endMs) {
          clearInterval(this.tournamentScheduleInterval);
          this.tournamentScheduleInterval = null;
          window.location.reload();
          return;
        }

        if (watchedBeforeEnd && toEnd <= 0) {
          clearInterval(this.tournamentScheduleInterval);
          this.tournamentScheduleInterval = null;
          window.location.reload();
        }
      };

      tick();
      this.tournamentScheduleInterval = setInterval(tick, 1000);
    },
  },
}
</script>

<style lang="scss" scoped>
.tournament-embed-view {
  border-radius: 8px;
  font-family: "Avenir", sans-serif;

  .tournament-timer-container ::v-deep > .tournament-timer {
    justify-content: flex-end;

    @media screen and (max-width: 600px) {
      justify-content: center;
    }
  }

  .banner-title {
  font-weight: 700 !important;
}

  .textStyle{
    font-size: 16px !important;
     font-weight: 300 !important;
    margin-top: 16px;
  }

  .textStyle{
    @media (max-width: 600px){
      font-size: 16px !important;
      margin-top: 0px;
    }
  }

  .register-desktop-icon{
    margin-left: 8px;
    margin-right: 0px !important;
  }

  .tournament-embed-header {
    position: relative;
    padding-bottom: 30px !important;

    .tournament-title {
      font-size: 24px;
      margin: 0;
    }

    .tournament-organizer {
      font-size: 16px;
      margin-top: 10px;
      color: #666;
    }

    .tournament-ended-label-overview {
      margin-top: 12px;
    }

    /* Enhanced Tournament Timer Container */
    .tournament-timer-container {
      margin-top: -96px;
      margin-bottom: 100px;
      
      // Small tablet breakpoint (sm: 600px - 768px)
      @media (min-width: 600px) and (max-width: 768px) {
        margin-top: 10px;
        margin-bottom: 40px;
      }
      
      // Large tablet breakpoint (md: 768px - 960px)
      @media (min-width: 768px) and (max-width: 960px) {
        margin-top: -20px;
        margin-bottom: 60px;
      }
      
      // Mobile breakpoint
      @media (max-width: 600px) {
        margin-top: 20px;
        margin-bottom: 20px;
      }
    }

    &.is-finished .tournament-timer-container {
      margin-top: 20px;
      margin-bottom: 24px;

      @media (min-width: 600px) and (max-width: 768px) {
        margin-top: 20px;
        margin-bottom: 24px;
      }

      @media (min-width: 768px) and (max-width: 960px) {
        margin-top: 20px;
        margin-bottom: 24px;
      }

      @media (max-width: 600px) {
        margin-top: 20px;
        margin-bottom: 20px;
      }
    }
  }

  /* Organizer Logo Styling */
  .organizer-logo-container {
    margin-bottom: 20px;

    .organizer-logo {
      max-height: 80px;
      max-width: 250px;
      object-fit: contain;
    }
  }

  /* Status Chip Styling */
  .status-chip {
    padding: 10px 15px;
    border-radius: 25px;
    margin-bottom: 20px;
    font-size: 14px;
    text-transform: capitalize;

    &.still-open {
      background-color: #FFF75F;
    }

    &.in-progress {
      background-color: var(--v-mainGreen-base);
    }

    &.closed {
      background-color: #111111;
    }

    &.archived {
      background-color: #6c757d;
    }
  }

  /* Mobile Tournament Header Styling */
  .mobile-tournament-header {
    padding: 0 15px;
    position: relative;
    margin-bottom: 20px;

    .mobile-tournament-title-container {
      margin: 20px 0 15px;
      position: relative;
    }

    .status-indicator-right {
      position: absolute;
      top: 0px;
      left: 15px;
      z-index: 2;

      .blink-circle {
        display: block;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        position: relative;
        z-index: 2;
        animation: pulse 2s infinite;
        box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
      }

      .splatter-effect {
        position: absolute;
        top: -5px;
        right: -5px;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        filter: blur(2px);
        opacity: 0.7;
        z-index: 1;
        animation: splatter 4s infinite alternate;
      }

      &.still-open {
        .blink-circle {
          background-color: #FFF75F;
        }

        .splatter-effect {
          background-color: rgba(255, 247, 95, 0.15);
        }
      }

      &.in-progress {
        .blink-circle {
          background-color: var(--v-mainGreen-base);
        }

        .splatter-effect {
          background-color: rgba(76, 175, 80, 0.4);
        }
      }

      &.closed {
        .blink-circle {
          background-color: #111111;
        }

        .splatter-effect {
          background-color: rgba(0, 0, 0, 0.4);
        }
      }
    }

    .mobile-tournament-title {
      font-size: 24px !important;
      font-weight: bold;
      text-align: center;
      margin: 0;
      padding: 0 10px 10px;
      color: var(--v-mainBlack-base);
      line-height: 1.3;
      word-wrap: break-word;
    }

    .mobile-title-underline {
      height: 3px;
      width: 60px;
      background: var(--v-mainGreen-base);
      margin: 5px auto 0;
      border-radius: 3px;
    }

    .mobile-tournament-organizer {
      font-size: 15px;
      text-align: center;
      color: #666;
      margin-bottom: 5px;

      .organizer-name {
        color: var(--v-mainBlack-base);
        font-weight: 500;
      }
    }

    .organizer-logo-container {
      margin-top: 10px;

      .organizer-logo {
        max-height: 60px;
        max-width: 200px;
        object-fit: contain;
      }
    }
  }

  /* Blinking animation for status indicator */
  @keyframes blink {

    0%,
    100% {
      opacity: 1;
    }

    50% {
      opacity: 0.5;
    }
  }

  .registration-banner {
    border-radius: 8px;
    padding: 16px;
    /* Background color is now applied via v-bind:style */
    border: 1px solid;
    /* Border color is now applied via v-bind:style */
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

    @media (max-width: 600px) {
      padding: 16px 12px;
      margin-bottom: 16px !important;
      position: fixed;
      z-index: 9;
      width: 100vw;
      bottom: -17px;
      left: 0;
      background-color: rgba(255, 255, 255, 0.55);
      /* Semi-transparent white */
      backdrop-filter: blur(8px);
      /* Add blur effect */
      -webkit-backdrop-filter: blur(8px);
      /* For Safari */
      box-shadow: 0 -4px 10px rgba(0, 0, 0, 0.05);
      /* Subtle shadow at the top */
      transition: transform 0.3s ease-in-out;

      &.hidden {
        transform: translateY(100%); // Move banner off-screen when hidden
      }
    }

    .banner-content,
    .banner-content-mobile {

      h3,
      h4 {
        /* Color is now applied via v-bind:style */
        font-weight: 500;
      }
    }

    .banner-content-mobile {
      h4 {
        font-size: 16px;
        line-height: 1.4;
        margin-bottom: 12px !important;
      }

      .register-mobile-btn {
        height: 48px;
        /* Increased height for better tap target */
        font-weight: bold;
        letter-spacing: 0.5px;
      }
    }
  }

  .tournament-embed-footer {
    margin-top: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .powered-by {
      font-size: 12px;
      color: #666;

      a {
        color: var(--v-mainGreen-base);
        text-decoration: none;
      }
    }
  }

  .enrolled-user-view {
    margin-bottom: 20px;
  }

  /* Mobile Footer */
  .mobile-embed-footer {
    margin-top: 10px;
    width: 100%;
    background-color: transparent;

    .footer-actions {
      display: flex;
      justify-content: space-around;

      .footer-action-btn {
        background-color: transparent !important;
        border: 1px solid #e0e0e0;
        padding: 12px 20px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        color: var(--v-mainBlack-base);
        transition: all 0.2s ease;
        min-width: 120px;

        &:active {
          transform: translateY(1px);
          box-shadow: 0 0 2px rgba(0, 0, 0, 0.1);
        }

        i {
          font-size: 16px;
        }
      }
    }
  }

  /* Desktop Footer - updated styles for better color compatibility */
  .desktop-embed-footer {
    margin-top: 20px;

    .desktop-action-btn {
      background-color: var(--v-fullWhite-base);
      border: 1px solid #e0e0e0;
      padding: 10px 16px;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 500;
      display: flex;
      text-align: center;
      align-items: center;
      justify-content: center;
      flex-grow: 1;
      color: var(--v-mainBlack-base);
      transition: all 0.2s ease;
      cursor: pointer;

      &:hover {
        opacity: 0.9;
      }

      i {
        font-size: 16px;
      }
    }
  }

  /* Remove the inline rules dialog styles as they're now in the RulesDialog component */
  ::v-deep .rules-dialog-container {
    .v-card {
      border-radius: 16px;
      overflow: hidden;
    }
  }

  /* Updated styles for rules dialog container - completely revised to fix shadow issues */
  ::v-deep(.rules-dialog-container) {
    margin: 0 !important;
    padding: 0 !important;
    overflow: hidden !important;

    &>.v-dialog {
      margin: 0 !important;
      border-radius: 12px !important;
      overflow: hidden !important;
      box-shadow: 0px 11px 15px -7px rgba(0, 0, 0, 0.2),
        0px 24px 38px 3px rgba(0, 0, 0, 0.14),
        0px 9px 46px 8px rgba(0, 0, 0, 0.12) !important;

      @media (max-width: 600px) {
        margin: 8px !important;
        max-width: calc(100% - 16px) !important;
      }

      /* Target the v-card inside dialog explicitly */
      .v-card {
        box-shadow: none !important;
        margin: 0 !important;
      }
    }
  }

  /* Animations for status indicator */
  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 1;
    }

    50% {
      transform: scale(1.2);
      opacity: 0.56;
    }

    100% {
      transform: scale(1);
      opacity: 1;
    }
  }

  @keyframes splatter {
    0% {
      transform: scale(1) rotate(0deg);
    }

    50% {
      transform: scale(1.5) rotate(180deg);
    }

    100% {
      transform: scale(1.2) rotate(360deg);
    }
  }

  /* Mobile Leaderboard Header - copied from Table component for consistency */
  .mobile-leaderboard-header {
    width: 100%;
    text-align: center;

    .leaderboard-title-container {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 5px;

      i {
        font-size: 18px;
        color: var(--v-mainBlack-base);
        margin-right: 8px;
      }

      h2 {
        font-size: 20px !important;
        font-weight: bold;
        margin: 0;
      }
    }

    .podium-note {
      font-size: 14px;
      color: #666;
      font-style: italic;
    }
  }

  .hide-on-mobile {
    @media (max-width: 600px) {
      display: none;
    }
  }

  .show-on-mobile {
    @media (min-width: 600px) {
      display: none;
    }
  }
  .redirect-overlay {
  position: fixed; 
  inset: 0;       
  z-index: 999999; 
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center; 
  text-align: center;
}


}
</style>
